import React, { useEffect, useState } from 'react'
import {
  CAlert, CButton, CCard, CCardBody, CCardHeader, CFormLabel, CFormSelect, CFormSwitch, CSpinner,
} from '@coreui/react'
import { api } from '../api/client'
import { fmtMoneda } from '../utils/moneda'

// [RGM] Panel TronIA (Fase 6 — diseño nuevo aprobado por Rafael).
// Tab "TronIA" de Mi Cuenta: estado del plan + consumo + chat con el asistente.
// Consume los endpoints /api/tronia/* (TroniaController — diseño TronIA nuevo,
// NO es réplica GX; los errores del backend llegan en el campo "mensaje").
// El chat NO es SSE aún: POST normal y respuesta JSON completa.

interface TroniaEstado {
  plan: number
  planNombre: string
  estado: string // PRUEBA | ACTIVO | INACTIVO | SUPERADMIN
  consultasUsadas: number
  consultasPlan: number
  tokensConsumidos: number
  tokensPlan: number
  consultasAdicionales: number
  precioConsultaAdicional: number
  adicionalesAutorizados: boolean
  maximoConsultasAdicionales: number
  maximoCostoAdicional: number
  habilitado: boolean
  mensaje: string
  // [RGM] 2026-10-04 (regla Rafael): true solo el día 6 del mes — habilita
  // bajar de plan inferior cuando TronIA está ACTIVO.
  puedeCambiarInferior?: boolean
}

interface TroniaAlerta {
  mensaje?: string
}

// Catálogo de planes TronIA (GET /tronia/planes — público). [RGM] Fase 2
// (2026-10-04): features = diferenciadores de cada plan (bullets "Incluye:").
interface TroniaPlanCatalogo {
  codigo: number
  nombre: string
  features?: string[]
}

const TroniaPanel: React.FC = () => {
  const [cargando, setCargando] = useState(true)
  const [estado, setEstado] = useState<TroniaEstado | null>(null)
  // [RGM] Activación del plan TronIA SIN CARGO por pasarela (decisión Rafael):
  // el cliente elige el plan y su valor se cobra en la mensualidad.
  const [planTronia, setPlanTronia] = useState(1)
  const [activandoPlan, setActivandoPlan] = useState(false)
  const [avisoPlan, setAvisoPlan] = useState('')
  const [errorPlan, setErrorPlan] = useState('')
  const [alertas, setAlertas] = useState<TroniaAlerta[]>([])
  const [alertasCerradas, setAlertasCerradas] = useState<number[]>([])
  const [avisoAdicionales, setAvisoAdicionales] = useState('')
  // [RGM] Fase 2: features del plan seleccionado (catálogo público). Si falla,
  // la sección "Incluye:" simplemente no se muestra.
  const [planesCatalogo, setPlanesCatalogo] = useState<TroniaPlanCatalogo[]>([])

  const cargarEstado = () => {
    setCargando(true)
    api.get<TroniaEstado>('/tronia/estado')
      .then((e) => { setEstado(e); if (e.plan) setPlanTronia(e.plan) })
      .catch(() => {})
      .finally(() => setCargando(false))
  }

  useEffect(() => {
    cargarEstado()
    // Alertas de consumo: hoy el endpoint devuelve [] — si llegan items se
    // muestran como CAlert (el ✕ solo las cierra LOCALMENTE; no hay endpoint
    // de "marcar leída" aún).
    api.get<TroniaAlerta[]>('/tronia/alertas')
      .then(setAlertas)
      .catch(() => {})
    // Catálogo de planes: solo para los bullets "Incluye:" del plan elegido.
    api.get<TroniaPlanCatalogo[]>('/tronia/planes')
      .then(setPlanesCatalogo)
      .catch(() => {})
  }, [])

  const alternarAdicionales = async () => {
    if (!estado) return
    setAvisoAdicionales('')
    try {
      const res = await api.post<{ mensaje: string }>('/tronia/adicionales', {
        autorizado: !estado.adicionalesAutorizados,
      })
      setAvisoAdicionales(res.mensaje ?? '')
      cargarEstado()
    } catch (err: any) {
      setAvisoAdicionales(err?.message ?? 'Error inesperado')
    }
  }

  // [RGM] Activa el plan TronIA elegido SIN CARGO: el backend guarda el plan
  // (POST /tronia/plan) y su valor se suma a la mensualidad (PagoService).
  // Sin pasarela: se eliminó el cobro separado del tipo 'tronia'.
  const activarPlan = async () => {
    setActivandoPlan(true)
    setAvisoPlan('')
    setErrorPlan('')
    try {
      const res = await api.post<{ mensaje: string }>('/tronia/plan', { planCodigo: planTronia })
      setAvisoPlan(res.mensaje ?? 'Plan TronIA activado. Se cobra en tu mensualidad.')
      cargarEstado()
    } catch (e: any) {
      setErrorPlan(e?.message ?? 'No se pudo activar el plan TronIA')
    } finally {
      setActivandoPlan(false)
    }
  }

  const superadmin = estado?.estado === 'SUPERADMIN'

  // [RGM] Fase 2 (2026-10-04): diferenciadores del plan SELECCIONADO en el
  // selector — se leen del catálogo público (no se duplican en el frontend).
  const featuresPlan =
    planesCatalogo.find((p) => p.codigo === planTronia)?.features ?? []

  // Regla Rafael 2026-10-04: con TronIA ACTIVO quedan deshabilitados el plan
  // actual (siempre) y los inferiores (salvo el día 6 del mes — el backend lo
  // expone en puedeCambiarInferior); subir de plan siempre está permitido.
  const opcionPlanHabilitada = (codigo: number): boolean => {
    if (estado?.estado !== 'ACTIVO') return true
    if (codigo === estado.plan) return false
    if (codigo < estado.plan && !estado.puedeCambiarInferior) return false
    return true
  }

  return (
    <>
      {/* Éxito de la activación: fuera de la tarjeta para que siga visible
          cuando el plan pasa a ACTIVO y la tarjeta desaparece. */}
      {avisoPlan && (
        <CAlert color="success" dismissible onClose={() => setAvisoPlan('')}>{avisoPlan}</CAlert>
      )}

      {/* ===== 1. Estado del plan y consumo ===== */}
      <CCard className="mb-3">
        <CCardHeader className="fw-semibold"><i className="fas fa-robot me-2" />TRONIA — Asistente inteligente para gestión de cobranza</CCardHeader>
        <CCardBody>
          {cargando ? (
            <div className="text-center py-4">
              <CSpinner size="sm" color="primary" />
              <div className="mt-2 text-body-secondary">Cargando información de consumo...</div>
            </div>
          ) : estado ? (
            <>
              {!estado.habilitado && estado.mensaje && (
                <CAlert color="info" className="mb-3">{estado.mensaje}</CAlert>
              )}

              {superadmin ? (
                <div className="text-body-secondary">Acceso global (SuperAdmin)</div>
              ) : (
                <>
                  <div className="mb-2">
                    <strong>Plan:</strong> {estado.planNombre} ·{' '}
                    <strong>Consultas:</strong> {estado.consultasUsadas} / {estado.consultasPlan} realizadas; adicionales usados: {estado.consultasAdicionales}
                  </div>
                </>
              )}
            </>
          ) : null}
        </CCardBody>
      </CCard>

      {/* ===== 1b. TronIA sin cargo: elegir/CAMBIAR plan (se cobra en la mensualidad) ===== */}
      {!superadmin && estado && (
        <CCard className="mb-3">
          <CCardHeader className="fw-semibold">Suscripción TronIA</CCardHeader>
          <CCardBody>
            {errorPlan && <CAlert color="danger" className="py-2">{errorPlan}</CAlert>}
            <p className="text-body-secondary small mb-2">
              {estado.estado === 'PRUEBA'
                ? 'Estás en el período de PRUEBA. Suscríbete para mantener el servicio sin interrupciones.'
                : estado.estado === 'ACTIVO' && estado.plan > 0
                  ? `Cambiar de plan (hoy: ${estado.planNombre}). El nuevo valor se cobra en tu mensualidad.`
                  : 'Tu plan TronIA está INACTIVO. Suscríbete para reactivarlo.'}
            </p>
            <div className="d-flex flex-wrap align-items-end gap-2">
              <div>
                <CFormLabel className="mb-1">Plan</CFormLabel>
                <CFormSelect
                  value={planTronia}
                  onChange={(e) => setPlanTronia(Number(e.target.value))}
                  style={{ minWidth: 280 }}
                >
                  <option value={1} disabled={!opcionPlanHabilitada(1)}>Inicial — $30.000 · 60 consultas</option>
                  <option value={2} disabled={!opcionPlanHabilitada(2)}>Profesional — $125.000 · 250 consultas</option>
                  <option value={3} disabled={!opcionPlanHabilitada(3)}>Empresarial — $300.000 · 600 consultas</option>
                </CFormSelect>
              </div>
              <CButton
                color="primary"
                disabled={activandoPlan || !opcionPlanHabilitada(planTronia)}
                onClick={activarPlan}
              >
                {activandoPlan
                  ? 'Guardando...'
                  : estado.estado === 'ACTIVO'
                    ? 'Cambiar de plan'
                    : 'Activar plan'}
              </CButton>
            </div>
            {/* [RGM] Fase 2 (2026-10-04): bullets del plan elegido, justo
                debajo del selector (textos que envía el catálogo). */}
            {featuresPlan.length > 0 && (
              <div className="small text-body-secondary mt-2">
                <span className="fw-semibold">Incluye:</span>
                <ul className="mb-0 ps-3">
                  {featuresPlan.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {/* Regla Rafael 2026-10-04: bajar de plan solo el día 6 del mes. */}
            {estado.estado === 'ACTIVO' && !estado.puedeCambiarInferior && (
              <small className="text-body-secondary d-block mt-2">
                Los cambios a un plan inferior solo se permiten el día 6 de cada mes.
              </small>
            )}
            <small className="text-body-secondary d-block mt-2">
              El plan queda ACTIVO de inmediato; su valor se cobra en tu mensualidad.
            </small>
          </CCardBody>
        </CCard>
      )}

      {/* ===== 1c. Consultas adicionales (la MISMA tarjeta del móvil:
                precio por consulta + topes + switch) ===== */}
      {!superadmin && estado && (
        <CCard className="mb-3">
          <CCardHeader className="fw-semibold">
            <i className="fas fa-plus-circle me-2" />Consultas adicionales
          </CCardHeader>
          <CCardBody>
            <div>Cada consulta adicional cuesta {fmtMoneda(estado.precioConsultaAdicional)}.</div>
            {(estado.maximoConsultasAdicionales > 0 || estado.maximoCostoAdicional > 0) && (
              <div className="small text-body-secondary mt-1">
                {[
                  estado.maximoConsultasAdicionales > 0
                    ? `Máximo ${estado.maximoConsultasAdicionales} consultas`
                    : null,
                  estado.maximoCostoAdicional > 0
                    ? `Costo máximo ${fmtMoneda(estado.maximoCostoAdicional)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
            {avisoAdicionales && (
              <CAlert color="success" className="py-2 mt-2 mb-1">{avisoAdicionales}</CAlert>
            )}
            <CFormSwitch
              className="mt-2"
              label="Autorizar consultas adicionales"
              checked={estado.adicionalesAutorizados}
              onChange={() => alternarAdicionales()}
            />
            <div className="small text-body-secondary">
              {estado.adicionalesAutorizados ? 'Autorizadas' : 'No autorizadas'}
            </div>
          </CCardBody>
        </CCard>
      )}

      {/* ===== Alertas de consumo (si hay) ===== */}
      {alertas
        .filter((_, i) => !alertasCerradas.includes(i))
        .map((alerta, i) => (
          <CAlert
            key={i}
            color="warning"
            dismissible
            onClose={() => setAlertasCerradas(prev => [...prev, i])}
          >
            {alerta.mensaje ?? 'Alerta de consumo'}
          </CAlert>
        ))}

      {/* El chat NO va aquí: vive en la página /tronia (Navbar →
          Herramientas → TronIA). Este tab solo configura y muestra consumo. */}
    </>
  )
}

export default TroniaPanel
